import {
  EDX_CANONICAL_TENANT_ID,
  PRODUCT_API_BFF_PREFIX,
} from './allowed-data-sources';
import type {
  ApiConfig,
  ApiConfigUrlKey,
  ExperienceConfig,
} from '../types/config';

export type Environment = 'prod' | 'beta' | 'int' | 'dev' | 'local';

/**
 * Map of ApiConfig keys to the subdomain slug used in the local stack URL.
 * A service listed here will be auto-built as
 *   https://local{prefix}-{slug}.local.api.highereducation.com
 * when `local` env is selected and a prefix is provided.
 *
 * Services NOT listed here fall back to the `int` URL and can still be
 * overridden individually via `localOverrides`.
 */
export const LOCAL_SERVICE_SLUGS: Partial<Record<keyof ApiConfig, string>> = {
  dynamicFlowUrl: 'dynamic-flows',
  organizationApiUrl: 'organizations',
  organizationServiceBaseUrl: 'organizations',
  campaignManagementApiUrl: 'campaign-management',
  offeringsSearchApiUrl: 'offerings',
  offeringsServiceBaseUrl: 'offerings-service',
  deliverySystemApiUrl: 'delivery-system',
  deliverySystemBaseUrl: 'delivery-system',
  audienceIntentApiUrl: 'audience-intent',
  prospectServiceApiUrl: 'prospect',
};

const ENDPOINT_MAP: Record<Exclude<Environment, 'local'>, ApiConfig> = {
  prod: {
    dynamicFlowUrl: 'https://dynamic-flows.api.highereducation.com',
    audienceIntentApiUrl: 'https://audience-intent.api.highereducation.com',
    offeringsSearchApiUrl: 'https://offerings.api.highereducation.com',
    offeringsServiceBaseUrl:
      'https://offerings-service.api.highereducation.com',
    mobiusApiUrl: 'https://api.mobius.highereducation.com',
    deliverySystemApiUrl: 'https://delivery-system.rveducation.io',
    organizationApiUrl: 'https://organizations.api.highereducation.com',
    campaignManagementApiUrl:
      'https://campaign-management.api.highereducation.com',
    prospectServiceApiUrl: 'https://prospect.api.highereducation.com',
    // Same-origin default: O&O tenants render on a hen-site origin and reach
    // the lead BFF (`/api/v1/delivery-service/lead/*`) via a relative path.
    // Affiliate embeds run on a foreign origin and override this with the
    // absolute hen-site origin at render time (see `resolveLeadApiBaseUrl`).
    leadApiBaseUrl: '',
    // Same-origin default for the location-inference BFF (`/api/v1/locate`),
    // mirroring `leadApiBaseUrl`. O&O tenants reach it via a relative path;
    // affiliate embeds override this with the absolute hen-site origin at
    // render time (see `resolveLocationInferenceApiUrl`).
    locationInferenceApiUrl: '',
    // Deny-all default (DB-2.1, EDUN-10689). Populate per-env when
    // a DSL author needs `navigation.externalLink` to open a domain.
    externalLinkOrigins: [],
  },
  beta: {
    dynamicFlowUrl: 'https://dynamic-flows.beta.api.highereducation.com',
    audienceIntentApiUrl:
      'https://audience-intent.beta.api.highereducation.com',
    offeringsSearchApiUrl: 'https://offerings.beta.api.highereducation.com',
    offeringsServiceBaseUrl:
      'https://offerings-service.beta.api.highereducation.com',
    mobiusApiUrl: 'https://api.beta.mobius.highereducation.com',
    deliverySystemApiUrl: 'https://delivery-system.beta.rveducation.io',
    organizationApiUrl: 'https://organizations.beta.api.highereducation.com',
    campaignManagementApiUrl:
      'https://campaign-management.beta.api.highereducation.com',
    prospectServiceApiUrl: 'https://prospect.beta.api.highereducation.com',
    leadApiBaseUrl: '',
    locationInferenceApiUrl: '',
    externalLinkOrigins: [],
  },
  int: {
    dynamicFlowUrl: 'https://dynamic-flows.int.api.highereducation.com',
    audienceIntentApiUrl: 'https://audience-intent.int.api.highereducation.com',
    offeringsSearchApiUrl: 'https://offerings.int.api.highereducation.com',
    offeringsServiceBaseUrl:
      'https://offerings-service.int.api.highereducation.com',
    mobiusApiUrl: 'https://api.staging.mobius.highereducation.com',
    deliverySystemApiUrl: 'https://delivery-system.staging.rveducation.io',
    organizationApiUrl: 'https://organizations.int.api.highereducation.com',
    campaignManagementApiUrl:
      'https://campaign-management.int.api.highereducation.com',
    prospectServiceApiUrl: 'https://prospect.int.api.highereducation.com',
    leadApiBaseUrl: '',
    locationInferenceApiUrl: '',
    externalLinkOrigins: [],
  },
  dev: {
    dynamicFlowUrl: 'https://dynamic-flows.dev.api.highereducation.com',
    audienceIntentApiUrl: 'https://audience-intent.dev.api.highereducation.com',
    // Legacy Algolia-style search service has no dev host (offerings.dev 404s),
    // so it stays on int. The Offerings Service base below (which serves the
    // DSL `/v1/offerings/search/match` + detail-by-id sources) does have a
    // seeded dev host, so it points there.
    offeringsSearchApiUrl: 'https://offerings.int.api.highereducation.com',
    offeringsServiceBaseUrl:
      'https://offerings-service.dev.api.highereducation.com',
    mobiusApiUrl: 'https://api.staging.mobius.highereducation.com',
    deliverySystemApiUrl: 'https://delivery-system.staging.rveducation.io',
    organizationApiUrl: 'https://organizations.dev.api.highereducation.com',
    campaignManagementApiUrl:
      'https://campaign-management.int.api.highereducation.com',
    prospectServiceApiUrl: 'https://prospect.dev.api.highereducation.com',
    leadApiBaseUrl: '',
    locationInferenceApiUrl: '',
    externalLinkOrigins: [],
  },
} as const;

/**
 * Absolute hen-site origin that serves the same-origin BFF routes an affiliate
 * embed cannot reach via a relative path — the lead BFF
 * (`/api/hen/v1/delivery-service/lead/*`) and the location-inference BFF
 * (`/api/v1/locate`) — per environment. Only affiliate embeds target this: they
 * render on a foreign origin (e.g. usnews.com) and cannot resolve the BFF
 * same-origin. O&O tenants render on a hen-site origin and keep the same-origin
 * relative path (`''`).
 */
const AFFILIATE_HEN_SITE_ORIGIN: Record<
  Exclude<Environment, 'local'>,
  string
> = {
  prod: 'https://www.highereducation.com',
  beta: 'https://dev.highereducation.com',
  int: 'https://dev.highereducation.com',
  dev: 'https://dev.highereducation.com',
};

/**
 * Resolve the absolute hen-site BFF origin for the running context: `''`
 * (same-origin) for O&O tenants, the env's absolute hen-site origin for
 * affiliate embeds. `affiliate` mirrors the tenants-API `tenant.affiliate`
 * signal (present iff the tenant is an affiliate).
 */
function affiliateHenSiteOrigin(env: Environment, affiliate: boolean): string {
  if (!affiliate) {
    return '';
  }
  const resolvedEnv = env === 'local' ? LOCAL_FALLBACK_ENV : env;
  return AFFILIATE_HEN_SITE_ORIGIN[resolvedEnv];
}

/** Fallback env used to seed `local` service URLs that aren't auto-built from the prefix. */
const LOCAL_FALLBACK_ENV: Exclude<Environment, 'local'> = 'int';

export interface LocalEnvOptions {
  /** e.g. "ichavez" -> https://localichavez-dynamic-flows.local.api.highereducation.com */
  localPrefix?: string;
  /** Per-service URL overrides that always win over the auto-built local URLs. */
  localOverrides?: Partial<ApiConfig>;
}

export function buildLocalApiUrl(slug: string, localPrefix: string): string {
  return `https://local${localPrefix}-${slug}.local.api.highereducation.com`;
}

export function buildLocalApiConfig({
  localPrefix = '',
  localOverrides = {},
}: LocalEnvOptions = {}): ApiConfig {
  const base = { ...ENDPOINT_MAP[LOCAL_FALLBACK_ENV] };
  const prefix = localPrefix.trim();

  if (prefix) {
    for (const [key, slug] of Object.entries(LOCAL_SERVICE_SLUGS) as Array<
      [ApiConfigUrlKey, string]
    >) {
      (base as Record<ApiConfigUrlKey, string | undefined>)[key] =
        buildLocalApiUrl(slug, prefix);
    }
  }

  for (const [key, value] of Object.entries(localOverrides) as Array<
    [keyof ApiConfig, ApiConfig[keyof ApiConfig]]
  >) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) {
        (base as Record<ApiConfigUrlKey, string | undefined>)[
          key as ApiConfigUrlKey
        ] = trimmed;
      }
      continue;
    }
    if (Array.isArray(value)) {
      (
        base as { externalLinkOrigins?: readonly string[] }
      ).externalLinkOrigins = value;
    }
  }

  return base;
}

export function getApiConfigForEnv(
  env: Environment,
  localOptions?: LocalEnvOptions,
): ApiConfig {
  if (env === 'local') {
    return buildLocalApiConfig(localOptions);
  }
  return ENDPOINT_MAP[env];
}

/**
 * Resolve the lead BFF base URL for the running context.
 *
 * The lead BFF is served by hen-site. O&O tenants render on a hen-site origin,
 * so they call it same-origin via a relative path (''). An affiliate embed
 * renders on a foreign origin and must target the absolute hen-site origin for
 * its environment. `affiliate` mirrors the tenants-API `tenant.affiliate`
 * signal (present iff the tenant is an affiliate).
 */
export function resolveLeadApiBaseUrl(
  env: Environment,
  { affiliate = false }: { affiliate?: boolean } = {},
): string {
  return affiliateHenSiteOrigin(env, affiliate);
}

/**
 * Resolve the location-inference BFF base URL (`GET /api/v1/locate`) for the
 * running context. Same rationale as `resolveLeadApiBaseUrl`: the BFF is served
 * by hen-site, so O&O tenants call it same-origin (`''`), but an affiliate embed
 * renders on a foreign origin (e.g. usnews.com) where the relative path would
 * resolve against the affiliate host and 404 — it must target the absolute
 * hen-site origin for its environment.
 */
export function resolveLocationInferenceApiUrl(
  env: Environment,
  { affiliate = false }: { affiliate?: boolean } = {},
): string {
  return affiliateHenSiteOrigin(env, affiliate);
}

/**
 * Same-origin Product API BFF for the canonical edX tenant id.
 *
 * Uses the same exact `tenantId` comparison as `isTenantAllowed`. A domain
 * match or a trimmed/cased variant would set this origin and then still be
 * rejected, with the error pointing at a missing base URL.
 *
 * The edX id is hardcoded for now. A follow-up should read
 * `productApiBffOrigin` from tenant config and keep this value as the default.
 */
export function resolveProductApiBffOrigin(
  tenantId?: string,
): string | undefined {
  return tenantId === EDX_CANONICAL_TENANT_ID
    ? PRODUCT_API_BFF_PREFIX
    : undefined;
}

export function getDefaultConfig(env: Environment = 'prod'): ExperienceConfig {
  return {
    api: getApiConfigForEnv(env),
    tracking: {
      dynamicFlowEventFlag: false,
    },
  };
}
